import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

// Create a separate axios instance for public auth endpoints (no auth token)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const authService = {
  /**
   * Check login method for an email address
   * Returns: { method: 'password' | 'sso' | 'both', ssoUrl?: string, ... }
   */
  async getLoginMethod(email) {
    try {
      const normalizedEmail = email?.toLowerCase().trim()
      const response = await publicApi.get(`/auth/login-method?email=${encodeURIComponent(normalizedEmail)}`)
      return response.data
    } catch (error) {
      console.error('Error checking login method:', error)
      // Default to password if check fails
      return { method: 'password', ssoAvailable: false }
    }
  },

  /**
   * Report a failed login attempt to track lockout status
   * Returns: { locked: boolean, failedAttempts: number, lockedUntil?: string, message?: string }
   */
  async reportLoginFailed(email) {
    try {
      const normalizedEmail = email?.toLowerCase().trim()
      const response = await publicApi.post('/auth/login-failed', { email: normalizedEmail })
      return response.data
    } catch (error) {
      console.error('Error reporting login failure:', error)
      // Return safe default - don't prevent login flow
      return { locked: false, failedAttempts: 0 }
    }
  },

  /**
   * Handle SSO callback - exchange authorization code for tokens
   * This is called after the user returns from the IdP
   */
  async handleSsoCallback(code, state) {
    try {
      const response = await publicApi.post('/auth/sso/callback', { code, state })
      return response.data
    } catch (error) {
      throw error
    }
  },

  /**
   * Sign up for a demo account
   */
  async demoSignup({ email, password, firstName, lastName, companyName, captchaToken, captchaType }) {
    const response = await publicApi.post('/public/demo-signup', {
      email: email?.toLowerCase().trim(),
      password,
      firstName,
      lastName,
      companyName,
      captchaToken,
      captchaType,
    })
    return response.data
  },

  /**
   * Verify email address with token
   */
  async verifyEmail(token, email) {
    const response = await publicApi.post('/public/verify-email', {
      token,
      email: email?.toLowerCase().trim(),
    })
    return response.data
  },

  /**
   * Resend verification email
   */
  async resendVerification(email) {
    const response = await publicApi.post('/public/resend-verification', {
      email: email?.toLowerCase().trim(),
    })
    return response.data
  },

  /**
   * Confirm an email-change request using the tokenized link sent to the new
   * address. `email` is the NEW (pending) address. (#312)
   */
  async confirmEmailChange(token, email) {
    const response = await publicApi.post('/public/confirm-email-change', {
      token,
      email: email?.toLowerCase().trim(),
    })
    return response.data
  },

  /**
   * Check if demo signups are currently enabled
   */
  async getDemoSignupStatus() {
    try {
      const response = await publicApi.get('/public/demo-signup-status')
      return response.data
    } catch (error) {
      return { enabled: false }
    }
  },

  /**
   * Check if self-serve signups are currently enabled
   */
  async getSelfServeSignupStatus() {
    try {
      const response = await publicApi.get('/public/self-serve-signup-status')
      return response.data
    } catch (error) {
      return { enabled: false }
    }
  },
}

export default authService
